/**
 * Rules for audio files attached to video generation (Venice / Seedance).
 *
 * Venice accepts audio in two roles:
 *  - `audio_url`: one background-music track, WAV or MP3, up to 30 s and 15 MB.
 *  - `reference_audio_urls`: voice / sound-effect donor clips, WAV or MP3, only alongside
 *    at least one reference image or clip: Seedance 2.0 up to 3 of 2–15 s, 15 s in total;
 *    Seedance 2.5 up to 10 of 2–30 s, 30 s in total (see ReferenceMediaLimits).
 */

import { DEFAULT_REFERENCE_LIMITS, type ReferenceMediaLimits } from './referenceClips';

export type AudioRole = 'music' | 'reference';

export const AUDIO_MIME_TYPES = ['audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/x-wav', 'audio/wave'] as const;
export const AUDIO_ACCEPT = '.mp3,.wav,audio/mpeg,audio/wav,audio/x-wav';
export const AUDIO_MAX_BYTES = 15 * 1024 * 1024;
export const MUSIC_MAX_SECONDS = 30;
export const REFERENCE_CLIP_MIN_SECONDS = 2;
export const REFERENCE_CLIP_MAX_SECONDS = 15;
export const REFERENCE_TOTAL_MAX_SECONDS = 15;
export const REFERENCE_MAX_CLIPS = 10;

export interface AudioFileLike {
    name: string;
    type: string;
    size: number;
}

/** Accept only what Venice accepts, judged by MIME type first and extension as a fallback. */
export const isSupportedAudioFile = (file: AudioFileLike): boolean => {
    const type = (file.type || '').toLowerCase();
    if ((AUDIO_MIME_TYPES as readonly string[]).includes(type)) return true;
    const ext = file.name.toLowerCase().split('.').pop();
    return !type && (ext === 'mp3' || ext === 'wav');
};

/** Returns a user-facing problem with the file, or null when it can be used. */
export const validateAudioFile = (file: AudioFileLike): string | null => {
    if (!isSupportedAudioFile(file)) return 'Only MP3 and WAV files are supported.';
    if (file.size > AUDIO_MAX_BYTES) return `The file is ${formatBytes(file.size)}; the limit is 15 MB.`;
    if (file.size === 0) return 'The file is empty.';
    return null;
};

export const audioExtensionFromMime = (mime: string, fallbackName = ''): 'mp3' | 'wav' => {
    const m = mime.toLowerCase();
    if (m.includes('wav')) return 'wav';
    if (m.includes('mpeg') || m.includes('mp3')) return 'mp3';
    return fallbackName.toLowerCase().endsWith('.wav') ? 'wav' : 'mp3';
};

export const formatBytes = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const formatDuration = (seconds: number | undefined): string => {
    if (seconds === undefined || !Number.isFinite(seconds)) return '';
    const s = Math.round(seconds);
    const m = Math.floor(s / 60);
    return m > 0 ? `${m}:${String(s % 60).padStart(2, '0')}` : `${s}s`;
};

export interface AudioClipInfo {
    label: string;
    durationSec?: number;
    sizeBytes?: number;
}

/**
 * Checks the connected clips against the limits of the chosen role before a request
 * is sent. Returns a list of problems (empty when everything is fine).
 */
export const checkAudioForVenice = (role: AudioRole, clips: AudioClipInfo[], referenceImageCount: number, limits: ReferenceMediaLimits = DEFAULT_REFERENCE_LIMITS): string[] => {
    const maxClips = limits.maxAudioClips;
    const clipMax = limits.audioClipMaxSec;
    const totalMax = limits.audioTotalMaxSec;
    const problems: string[] = [];
    if (clips.length === 0) return problems;
    for (const c of clips) {
        if (c.sizeBytes !== undefined && c.sizeBytes > AUDIO_MAX_BYTES) problems.push(`“${c.label}” is over 15 MB.`);
    }
    if (role === 'music') {
        if (clips.length > 1) problems.push('Background music takes one file; only the first connected audio is used.');
        const first = clips[0];
        if (first.durationSec !== undefined && first.durationSec > MUSIC_MAX_SECONDS) {
            problems.push(`“${first.label}” is ${formatDuration(first.durationSec)}; background music can be at most ${MUSIC_MAX_SECONDS}s.`);
        }
        return problems;
    }
    if (referenceImageCount === 0) problems.push('Reference audio needs at least one reference image or clip connected as well.');
    if (clips.length > maxClips) problems.push(`At most ${maxClips} reference audio clips can be used.`);
    let total = 0;
    for (const c of clips) {
        if (c.durationSec === undefined) continue;
        total += c.durationSec;
        if (c.durationSec < REFERENCE_CLIP_MIN_SECONDS || c.durationSec > clipMax) {
            problems.push(`“${c.label}” is ${formatDuration(c.durationSec)}; reference audio must be ${REFERENCE_CLIP_MIN_SECONDS}–${clipMax}s.`);
        }
    }
    if (total > totalMax) problems.push(`The reference audio clips add up to ${formatDuration(total)}; the total can be at most ${totalMax}s.`);
    return problems;
};
